import type { User } from '@lmc/ts-common'
import requests from 'api'
import { postJSON } from 'common/services/api'
import { normalizeErrorMessage, showToast } from 'common/utils'
import { loadList } from 'operations/Data'
import {
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient
} from 'react-query'
import { useDispatch } from 'react-redux'

type UseUserProps = {
  id?: string | undefined
  includes?: Array<string> | undefined
  options?: Pick<UseQueryOptions<{ result: User }>, 'onSuccess' | 'enabled'>
  onUpdateSuccess?: () => void
  onDeleteSuccess?: () => void
  onCreateSuccess?: () => void
  // Return `true` to signal the error is fully handled and the default error
  // toast should be suppressed (e.g. the caller renders its own inline message).
  // `void` keeps existing callers that return nothing valid (toast still shows).
  // biome-ignore lint/suspicious/noConfusingVoidType: intentional "return true to suppress, else nothing" contract
  onErrorCustom?: (e: unknown) => boolean | void
}

export type UpdateUserReturnType =
  | {
      result: User
    }
  | unknown

type UseUserReturn = {
  user: User | undefined
  isLoading: boolean
  isLoadingUpdate: boolean
  isLoadingDelete: boolean
  isLoadingCreate: boolean
  refetch: () => void
  updateUser: (values: Partial<User>) => Promise<UpdateUserReturnType>
  createUser: (values: Partial<User>) => Promise<UpdateUserReturnType>
  deleteUser: () => Promise<unknown>
}

export const SEND_USER_URL = 'list/carers'
const USER_QUERY_KEY = 'user'
// A persisted carer id is a 24-char Mongo ObjectId hex string; the fetch is
// only enabled for a real id (guards against placeholders like '' or 'me').
const MONGO_ID_LENGTH = 24

const useUser = ({
  id,
  includes = [],
  options,
  onUpdateSuccess,
  onDeleteSuccess,
  onCreateSuccess,
  onErrorCustom
}: UseUserProps): UseUserReturn => {
  const dispatch = useDispatch()

  const { data, isLoading, refetch } = useQuery<
    { result: User },
    [string, string, string[]]
  >(
    [USER_QUERY_KEY, id, includes],
    ({ queryKey }) => {
      const [, id, includes] = queryKey as [string, string, string[]]
      return requests.users.fetch({ params: { id, includes } })
    },
    {
      onSuccess: options?.onSuccess,
      // Callers that only need the mutations (e.g. the Edit-permissions panel,
      // which already has the carer) can opt out of the fetch via options.enabled.
      enabled:
        (options?.enabled ?? true) &&
        Boolean(id) &&
        id?.length === MONGO_ID_LENGTH
    }
  )

  const handleError = (err: unknown) => {
    // A caller that fully handles the error (returns true) suppresses the
    // default toast; otherwise we still surface it after onErrorCustom runs.
    if (onErrorCustom?.(err) === true) {
      return
    }
    showToast({
      title: normalizeErrorMessage(err),
      type: 'error'
    })
  }

  const updateFunction = async (
    values: Partial<User>
  ): Promise<UpdateUserReturnType> =>
    postJSON({
      url: `${SEND_USER_URL}/${id}`,
      method: 'PUT',
      json: values
    })

  const deleteFunction = () =>
    postJSON({
      url: `${SEND_USER_URL}/${id}`,
      method: 'DELETE'
    })

  const { mutateAsync: updateMutation, isLoading: isLoadingUpdate } =
    useMutation<UpdateUserReturnType, unknown, Partial<User>, unknown>(
      [`update-user-${id}`],
      updateFunction,
      {
        onSuccess: () => {
          dispatch(loadList('users') as any)
          onUpdateSuccess?.()
        },
        onError: (err) => handleError(err)
      }
    )

  const { mutateAsync: deleteMutation, isLoading: isLoadingDelete } =
    useMutation([`delete-user-${id}`], deleteFunction, {
      onSuccess: () => {
        dispatch(loadList('users') as any)
        onDeleteSuccess?.()
      },
      onError: (err) => handleError(err)
    })

  const user = data?.result

  const queryClient = useQueryClient()

  const handleUpdate = async (values: Partial<User>) => {
    try {
      const result = await updateMutation(values)
      queryClient.invalidateQueries(USER_QUERY_KEY)
      return result
    } catch (err) {
      return err
    }
  }

  const createFunction = async (
    values: Partial<User>
  ): Promise<UpdateUserReturnType> =>
    postJSON({
      url: `${SEND_USER_URL}`,
      method: 'POST',
      json: values
    })

  const { mutateAsync: createMutation, isLoading: isLoadingCreate } =
    useMutation<UpdateUserReturnType, unknown, Partial<User>, unknown>(
      [`create-user-${id}`],
      createFunction,
      {
        onSuccess: () => {
          dispatch(loadList('users') as any)
          onCreateSuccess?.()
        },
        onError: (err) => {
          handleError(err)
        }
      }
    )

  return {
    user,
    isLoading,
    isLoadingUpdate,
    isLoadingDelete,
    isLoadingCreate,
    refetch,
    updateUser: handleUpdate,
    deleteUser: deleteMutation,
    createUser: createMutation
  }
}

export const convertFormTypeToUserBody = <FormType extends object>(
  values: FormType,
  converter: (formTypeValues: FormType) => Partial<User>
): Partial<User> => {
  const convertedFormType = converter(values)

  return convertedFormType
}

export default useUser
